// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { setLocalStorage, getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import { getBrand, getIsGuestUser, isCanada } from '@tcp/core/src/utils/utils';
import {
  getHandlerArgs,
  isSupported,
  toPlainString,
  visibility,
} from '@tcp/web/src/components/common/molecules/PageVisibility/utils.web';
import { readCookie } from '@tcp/core/src/utils/cookie.util';
import { getSessionStorage, setSessionStorage } from '@tcp/core/src/utils';
import {
  getFlashingTimeInMs,
  getInactiveTitleFlashing,
  getIsPageVisibilityTitleEnabled,
  getPageVisibilityConfig,
} from '@tcp/core/src/reduxStore/selectors/session.selectors';
import { getDynamicLabelValues } from '@tcp/core/src/utils/labels/labelUtilty';
import { getStoreRef } from '@tcp/core/src/utils/store.utils';
import {
  PAGE_TITLES,
  BRAND_NAMES,
  DEFAULT_TITLE,
} from '@tcp/core/src/constants/pageTitle.constants';
import { deriveSEOTags } from '@tcp/core/src/config/SEOTags.config';
import pushEventToAdobeAnalytics from '@tcp/core/src/adobeAnalytics/adobe.utils';
import { getOrderTotalAmount } from '@tcp/core/src/components/features/CnC/SinglePageCheckout/container/SinglePageCheckout.selector';

const TIME_ZONE = 'America/New_York';
const EN_US = 'en-US';
const PAGE_VISIBILITY_PAGES = {
  PDP: 'ProductDetail',
  BAG: 'Bag',
  CHECKOUT: 'SinglePageCheckout',
};

const getShouldReloadPageAtSpecificTimeCondition = ({
  currentTimestampInEST,
  specifiedTimeToReloadInEST,
  timeToLeaveNumber,
  applicationReloadedTimeNumber,
}) => {
  return (
    currentTimestampInEST > specifiedTimeToReloadInEST &&
    (timeToLeaveNumber < specifiedTimeToReloadInEST ||
      applicationReloadedTimeNumber < specifiedTimeToReloadInEST)
  );
};

const getStorageValueInInteger = (value) => {
  return value && parseInt(value, 10);
};

const getShouldReloadPage = ({ timeToReloadPageInEST, currentTimestampInEST }) => {
  let specifiedTimeToReloadInEST;
  let shouldReloadPage;
  if (timeToReloadPageInEST) {
    specifiedTimeToReloadInEST = new Date(
      new Date().toLocaleDateString(EN_US, { timeZone: TIME_ZONE }) + timeToReloadPageInEST
    ).getTime();
  }

  if (specifiedTimeToReloadInEST) {
    const timeToLeave = getLocalStorage('timeToLeave');
    const applicationReloadedTime = getSessionStorage('applicationReloadedTime');

    const timeToLeaveNumber = getStorageValueInInteger(timeToLeave);

    const applicationReloadedTimeNumber = getStorageValueInInteger(applicationReloadedTime);

    shouldReloadPage = getShouldReloadPageAtSpecificTimeCondition({
      currentTimestampInEST,
      specifiedTimeToReloadInEST,
      timeToLeaveNumber,
      applicationReloadedTimeNumber,
    });
  }

  return shouldReloadPage;
};

const performReload = ({
  isReloading,
  userLoggedOut,
  shouldReloadPage,
  callbackFunc,
  currentTimestampInEST,
  isReloadOnInactivityPostSpecifiedTime,
}) => {
  if (isReloading || userLoggedOut || shouldReloadPage) {
    callbackFunc(isReloading || shouldReloadPage);
    if (isReloadOnInactivityPostSpecifiedTime) {
      setSessionStorage({ key: 'applicationReloadedTime', value: currentTimestampInEST });
    }
    window.location.reload();
  }
};

const getPageIdFromPathname = (pathname) => {
  const pageIdMap = {
    Bag: 'Bag',
    SinglePageCheckout: 'Checkout',
    ProductDetail: 'p',
  };

  return pageIdMap[pathname];
};

const getPageName = () => {
  if (window && window.location && window.location.pathname) {
    const pathname = window.location.pathname || '';
    if (pathname.includes('/p/')) return PAGE_VISIBILITY_PAGES.PDP;
    if (pathname.includes('/bag')) return PAGE_VISIBILITY_PAGES.BAG;
    if (pathname.includes('/checkout')) return PAGE_VISIBILITY_PAGES.CHECKOUT;
  }
  return 'Home';
};

const isShowInactiveTitle = (
  abTest,
  currentPage = '',
  inActivePages = [],
  isInactiveTitleFlashingEnabled = false
) => {
  return inActivePages.includes(currentPage) && (abTest || isInactiveTitleFlashingEnabled);
};

const getPageTitle = (pageName, pageId, router, pageVisibilityTitle, store) => {
  let pageTitle = '';
  if (pageName === PAGE_VISIBILITY_PAGES.BAG) {
    const brand = getBrand();
    const tcpTitle = `${BRAND_NAMES[brand]} ${isCanada() ? 'CA' : ''}`;
    pageTitle = `${PAGE_TITLES[pageId.toLowerCase()] || DEFAULT_TITLE} | ${tcpTitle}`;
  } else if (pageName !== PAGE_VISIBILITY_PAGES.PDP) {
    const nonPdpSeoData = deriveSEOTags(pageId, store, router);
    pageTitle = nonPdpSeoData.title;
  } else {
    pageTitle = pageVisibilityTitle;
  }
  return pageTitle;
};

class PageVisibility extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSupportedState: isSupported && visibility,
    };
    this.timerRef = React.createRef(null);
    this.store = getStoreRef();
    this.timeLeft = React.createRef(null);
  }

  componentDidMount() {
    const { isSupportedState } = this.state;
    const { isReloadOnInactivity, isReloadOnInactivityPostSpecifiedTime } = this.props;
    if (!isSupportedState && (!isReloadOnInactivity || !isReloadOnInactivityPostSpecifiedTime)) {
      return;
    }

    const currentTimeInEST = new Date().toLocaleString(EN_US, { timeZone: TIME_ZONE });
    const currentTimestampInEST = new Date(currentTimeInEST).getTime();
    if (isReloadOnInactivityPostSpecifiedTime) {
      setSessionStorage({ key: 'applicationReloadedTime', value: currentTimestampInEST });
    }

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener(visibility.event, this.handleVisibilityChange);
  }

  componentWillUnmount() {
    const { isSupportedState } = this.state;
    const { isReloadOnInactivity, isReloadOnInactivityPostSpecifiedTime } = this.props;

    if (!isSupportedState && (!isReloadOnInactivity || !isReloadOnInactivityPostSpecifiedTime)) {
      return;
    }
    document.removeEventListener(visibility.event, this.handleVisibilityChange);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  handleVisibilityChange() {
    const [isVisible] = getHandlerArgs();
    const {
      callbackFunc,
      inactivityTimeInterval,
      isReloadOnInactivityPostSpecifiedTime,
      timeToReloadPageInEST,
      isReloadOnInactivity,
      inActivePagesList,
      isPageVisibilityTitleEnabled,
      router,
      pageVisibilityTitle,
      cartTotal,
      isInactiveTitleFlashingEnabled,
      flashingInterval,
    } = this.props;
    const currentTimeInEST = new Date().toLocaleString(EN_US, { timeZone: TIME_ZONE });
    const currentTimestampInEST = new Date(currentTimeInEST).getTime();
    const pageName = getPageName();
    if (
      isShowInactiveTitle(
        isPageVisibilityTitleEnabled,
        pageName,
        inActivePagesList,
        isInactiveTitleFlashingEnabled
      )
    ) {
      const PAGE_HIDDEN_TITLE =
        getDynamicLabelValues('dynamic.global_d.lbl_global_page_hidden_title') || '';
      const pageId = getPageIdFromPathname(pageName);
      const pageTitle = getPageTitle(pageName, pageId, router, pageVisibilityTitle, this.store);
      if (!isVisible) {
        let toggle = false;
        this.timeLeft.current =
          new Date(new Date().toLocaleString(EN_US, { timeZone: TIME_ZONE })).getTime() / 1000; // in seconds
        const cartItems = readCookie('cartItemsCount') || 0;
        if (isInactiveTitleFlashingEnabled && PAGE_HIDDEN_TITLE) {
          this.timerRef.current = setInterval(() => {
            document.title = toggle ? toPlainString(pageTitle) : toPlainString(PAGE_HIDDEN_TITLE);
            toggle = !toggle;
          }, flashingInterval);
        } else if (isPageVisibilityTitleEnabled) {
          this.timerRef.current = setTimeout(() => {
            document.title = PAGE_HIDDEN_TITLE;
          }, 5000);
        }
        let itemsInCart = `${cartItems}`;
        if (cartTotal && cartTotal > 0) {
          itemsInCart += `|${cartTotal}`;
        }
        pushEventToAdobeAnalytics('in_active_page_track', {
          msg: PAGE_HIDDEN_TITLE,
          timeSincePageLoad: 5,
          itemsInCart,
        });
      } else {
        clearTimeout(this.timerRef.current);
        document.title = toPlainString(pageTitle);
        if (this.timeLeft.current) {
          const timestamp =
            new Date(new Date().toLocaleString(EN_US, { timeZone: TIME_ZONE })).getTime() / 1000; // in seconds
          const timeAway = timestamp - this.timeLeft.current; // 1000; in seconds
          pushEventToAdobeAnalytics('track_time_away_from_website', { timestamp, timeAway });
        }
      }
    }
    if (isVisible) {
      let isReloading;
      let userLoggedOut;
      let shouldReloadPage;
      if (isReloadOnInactivity) {
        const timeToLeave = getLocalStorage('timeToLeave');
        isReloading = currentTimestampInEST - parseInt(timeToLeave, 10) >= inactivityTimeInterval;
        const isGuest = getIsGuestUser();
        const cid = readCookie('cid');
        userLoggedOut = !isGuest && !cid;
      }
      if (isReloadOnInactivityPostSpecifiedTime) {
        shouldReloadPage = getShouldReloadPage({
          timeToReloadPageInEST,
          currentTimestampInEST,
        });
      }

      performReload({
        isReloading,
        userLoggedOut,
        shouldReloadPage,
        callbackFunc,
        isReloadOnInactivityPostSpecifiedTime,
      });
    } else {
      // set time in local storage
      setLocalStorage({ key: 'timeToLeave', value: currentTimestampInEST });
    }
  }

  render() {
    const { children } = this.props;

    if (!children) {
      return null;
    }

    return React.Children.only(children);
  }
}

PageVisibility.defaultProps = {
  isReloadOnInactivity: false,
  isReloadOnInactivityPostSpecifiedTime: false,
  inactivityTimeInterval: 24 * 60 * 60 * 1000,
  timeToReloadPageInEST: '',
};

PageVisibility.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  isReloadOnInactivity: PropTypes.bool,
  isReloadOnInactivityPostSpecifiedTime: PropTypes.bool,
  inactivityTimeInterval: PropTypes.number,
  timeToReloadPageInEST: PropTypes.string,
};

const mapStateToProps = (state) => ({
  pageVisibilityTitle: state.SEOData?.pageVisibilityTitle,
  isPageVisibilityTitleEnabled: getIsPageVisibilityTitleEnabled(state),
  inActivePagesList: getPageVisibilityConfig(state),
  cartTotal: getOrderTotalAmount(state),
  isInactiveTitleFlashingEnabled: getInactiveTitleFlashing(state),
  flashingInterval: getFlashingTimeInMs(state),
});

export default connect(mapStateToProps, null)(PageVisibility);
